/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ExtensionSearchInput.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/


import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../../../components/ui/input';

export type ExtensionOption = {
    value: string;
    label: string;
    description?: string;
    searchText: string;
};

interface ExtensionSearchInputProps {
    id: string;
    value: string;
    onChange: (value: string) => void;
    options: ExtensionOption[];
    placeholder: string;
}

export function ExtensionSearchInput({ id, value, onChange, options, placeholder }: ExtensionSearchInputProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isTypingRef = useRef(false);

    // Sync localValue with value prop only when not actively typing
    useEffect(() => {
        if (!isTypingRef.current) {
            setLocalValue(value);
        }
    }, [value]);

    const filteredOptions = useMemo(() => {
        const term = localValue.trim().toLowerCase();
        if (!term) return options.slice(0, 15);
        return options.filter((o) => o.searchText.includes(term)).slice(0, 15);
    }, [options, localValue]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                isTypingRef.current = false;
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setLocalValue(newValue);
        setIsOpen(true);
        isTypingRef.current = true;
        onChange(newValue);
        setTimeout(() => { isTypingRef.current = false; }, 100);
    };

    const handleSelectOption = (selectedValue: string) => {
        setLocalValue(selectedValue);
        isTypingRef.current = false;
        onChange(selectedValue);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={containerRef}>
            <Input
                id={id}
                value={localValue}
                onFocus={() => { setIsOpen(localValue.trim().length > 0 || options.length > 0); }}
                onChange={handleInputChange}
                onBlur={() => { setTimeout(() => { setIsOpen(false); isTypingRef.current = false; }, 200); }}
                placeholder={placeholder}
                autoComplete="off"
                spellCheck={false}
            />
            {isOpen && filteredOptions.length > 0 && (
                <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                    <ul className="py-1 text-sm">
                        {filteredOptions.map((option) => (
                            <li key={option.value}>
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between gap-3 px-3 py-2 hover:bg-muted"
                                    onMouseDown={(event) => { event.preventDefault(); handleSelectOption(option.value); }}
                                >
                                    <span className="font-medium text-foreground">{option.value}</span>
                                    {option.description && (
                                        <span className="text-xs text-muted-foreground">{option.description}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
